import { useEffect, useMemo, useRef, useState } from 'react'
import { API_BASE_URL, authFetch } from '@/api/client'
import {
  MIME_DOCX,
  MIME_PDF,
  type DocumentDetail,
  type PreviewMode,
} from '@/api/docfiller-types'
import { captureSafeException, captureSafeMessage } from '@/lib/sentry-instrument'

export interface LivePreview {
  /** Live-rendered preview bytes from ``POST /preview``. Pass directly to
   *  the preview component (react-pdf / docx-preview both accept Blob)
   *  instead of an intermediate ``URL.createObjectURL``. ``null`` until
   *  the first POST resolves, when original mode is selected, or after the
   *  active document changes. */
  data: Blob | null
  /** Public R2 presigned URL for the SOURCE document. Used as the visible
   *  fallback while the first live render is still in flight (and on
   *  preview error). ``null`` when no preview is possible at all. */
  sourceUrl: string | null
  /** ``true`` while a freshly-rendered preview is being fetched. */
  isUpdating: boolean
  /** Whether the visible preview is the locally rendered Blob (vs. source). */
  isFilled: boolean
  /** Last error from the preview endpoint, if any. */
  error: Error | null
}

/**
 * Re-renders the document preview every time the saved field values
 * change. Hits ``POST /dashboard/documents/{uuid}/preview`` which returns
 * the bytes inline (no R2 round-trip).
 */
export function useLivePreview(
  doc: DocumentDetail | undefined,
  mode: PreviewMode = 'filled',
): LivePreview {
  const [filledData, setFilledData] = useState<Blob | null>(null)
  const [isUpdating, setIsUpdating] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  const valuesSignature = useMemo(() => {
    if (!doc) return ''
    return doc.fields
      .map((f) =>
        mode === 'final'
          ? `${f.uuid}@${f.revision}=${f.current_value ?? ''}:${f.suggested_value ?? ''}`
          : `${f.uuid}@${f.revision}=${f.current_value ?? ''}`,
      )
      .sort()
      .join('|')
  }, [doc, mode])

  const docUuid = doc?.uuid ?? null
  const docMime = doc?.mime_type ?? null
  const currentSourceUrl = doc?.presigned_url ?? null

  const [retainedSource, setRetainedSource] = useState<{
    docUuid: string | null
    sourceUrl: string | null
  }>({ docUuid, sourceUrl: currentSourceUrl })
  let sourceUrl = retainedSource.sourceUrl
  if (retainedSource.docUuid !== docUuid) {
    sourceUrl = currentSourceUrl
    setRetainedSource({ docUuid, sourceUrl: currentSourceUrl })
  } else if (currentSourceUrl && retainedSource.sourceUrl !== currentSourceUrl) {
    sourceUrl = currentSourceUrl
    setRetainedSource({ docUuid, sourceUrl: currentSourceUrl })
  }
  const hasSource = sourceUrl != null

  const supportsLivePreview =
    hasSource && (docMime === MIME_PDF || docMime === MIME_DOCX)

  const [prevDocUuid, setPrevDocUuid] = useState<string | null>(docUuid)
  if (prevDocUuid !== docUuid) {
    setPrevDocUuid(docUuid)
    setFilledData(null)
    setError(null)
  }

  const runIdRef = useRef(0)

  useEffect(() => {
    if (!docUuid || !supportsLivePreview || mode === 'original') {
      return
    }

    let cancelled = false
    runIdRef.current += 1
    const myRun = runIdRef.current

    const run = async () => {
      setIsUpdating(true)
      setError(null)
      let alreadyCaptured = false
      try {
        const res = await authFetch(
          `${API_BASE_URL}/dashboard/documents/${docUuid}/preview`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ mode }),
          },
        )
        if (!res.ok) {
          if (res.status >= 500 || res.status === 410) {
            captureSafeMessage('preview.fetch_failed', 'error', {
              operation: 'preview_fetch',
              route: '/dashboard/documents/{uuid}/preview',
              doc_uuid: docUuid,
              status: res.status,
            })
            alreadyCaptured = true
          }
          throw new Error(messageForPreviewStatus(res.status))
        }
        const blob = await res.blob()
        if (cancelled || myRun !== runIdRef.current) return
        setFilledData(blob)
      } catch (e) {
        if (cancelled || myRun !== runIdRef.current) return
        const err = e instanceof Error ? e : new Error(String(e))
        setError(err)
        if (!alreadyCaptured && err.name !== 'AbortError') {
          captureSafeException(err, 'preview.fetch_failed', {
            operation: 'preview_fetch',
            route: '/dashboard/documents/{uuid}/preview',
            doc_uuid: docUuid,
          })
        }
      } finally {
        if (!cancelled && myRun === runIdRef.current) setIsUpdating(false)
      }
    }
    void run()

    return () => {
      cancelled = true
    }
  }, [docUuid, supportsLivePreview, valuesSignature, mode])

  if (!doc) {
    return {
      data: null,
      sourceUrl: null,
      isUpdating: false,
      isFilled: false,
      error: null,
    }
  }

  if (!supportsLivePreview) {
    return {
      data: null,
      sourceUrl,
      isUpdating: false,
      isFilled: false,
      error: null,
    }
  }

  if (mode === 'original') {
    return {
      data: null,
      sourceUrl,
      isUpdating: false,
      isFilled: false,
      error: null,
    }
  }

  return {
    data: filledData,
    sourceUrl,
    isUpdating,
    isFilled: filledData != null && error == null,
    error,
  }
}

function messageForPreviewStatus(status: number): string {
  if (status === 401) return 'Session expired - sign in again to refresh preview.'
  if (status === 403) return "You don't have permission to preview this document."
  if (status === 404) return 'Document not found.'
  if (status === 410) return 'Document content has been removed.'
  if (status === 413) return 'Document is too large to preview.'
  if (status >= 500) return 'Preview is temporarily unavailable.'
  return 'Could not render preview.'
}
